import Device from './Device';

/** Class representing a printer. */
class Printer extends Device {
    static PROFILE_CARD          = 1;   // 00000001;
    static PROFILE_THERMAL       = 2;   // 00000010;
    static PROFILE_OTHER         = 4;   // 00000100;
    static PROFILE_OPT_HOLE      = 8;   // 00001000;
    static PROFILE_OPT_CUTTER    = 16;  // 00010000;
    static PROFILE_OPT_CUT_WHOLE = 32;  // 00100000;
    static PROFILE_OPT_COLOR     = 64;  // 01000000
    static PROFILE_OPT_DUPLEX    = 128; // 10000000

    /** Boca thermal default: black mark on back (PROFILE_THERMAL only). */
    static PROFILE_BOCA_BLACK_MARK = 2;

    static FAMILY_BOCA           = 'boca';
    static FAMILY_ZEBRA_THERMAL  = 'zebraThermal';
    static FAMILY_ZEBRA_CARD     = 'zebraCard';
    static FAMILY_OTHER          = 'other';

    /**
     * List of available profiles
     * ==========================
     *
     * 2   => 00000010  Boca thermal (black mark on back)
     * 10  => 00001010  Thermal printer (type ZDxx or ZX) with hole paper without cutter
     * 3   => 00000011  Thermal printer (type ZDxx or ZX) with continuous paper without cutter
     *      (legacy value: CARD|THERMAL; isCardPrinter ignores CARD when THERMAL is set)
     * 18  => 00010010  Thermal printer (type ZDxx or ZX) with continuous paper and cutter (cut after each ticket)
     * 26  => 00011010  Thermal printer (type ZDxx or ZX) with hole paper and cutter (cut after each ticket)
     * 50  => 00110010  Thermal printer (type ZDxx or ZX) with continuous paper and cutter (cut after the whole document)
     * 58  => 00111010  Thermal printer (type ZDxx or ZX) with hole paper and cutter (cut after the whole document)
     * 1   => 00000001  Card printer (type ZCxxx) with Black Ribbon and Duplex disabled
     * 65  => 01000001  Card printer (type ZCxxx) with Color Ribbon and Duplex disabled
     * 129 => 10000001  Card printer (type ZCxxx) with Black Ribbon and Duplex enabled
     * 193 => 11000001  Card printer (type ZCxxx) with Color Ribbon and Duplex enabled
     * 4   => 00000100  Other standard printer
     * 6   => 00000110  Other thermal printer
     */

    getIconName() {
        if (this.isCashDrawerQueue())
            return 'inbox';
        if (this.isBocaThermalPrinter())
            return 'receipt';
        return 'print';
    }

    /**
     * Card print mode (ZC). Profile 3 is CARD|THERMAL by legacy mistake — not a card.
     *
     * @return {boolean}
     */
    isCardPrinter() {
        return Printer.profileIsCard(this.options?.profile);
    }

    /**
     * @param {number|undefined|null} profile
     * @return {boolean}
     */
    static profileIsCard(profile) {
        const p = profile || 0;
        return !!(p & Printer.PROFILE_CARD) && !(p & Printer.PROFILE_THERMAL);
    }

    /**
     * Zebra card printer from CUPS make-and-model / device-id (ZC / Jaguar).
     *
     * @return {boolean}
     */
    isZebraCardPrinter() {
        const mm = this.cupsMakeAndModel();
        const did = this.cupsDeviceId().toUpperCase();
        if (/jaguar|digital\s*card\s*printer/i.test(mm) || /jaguar|digital\s*card/i.test(did))
            return true;
        if (/\bZC\d{2,}/i.test(mm) || /\bMDL:ZC/i.test(did))
            return true;
        return false;
    }

    /**
     * Printer family used to filter print-mode options in the UI.
     *
     * @return {string}
     */
    printerFamily() {
        if (this.isBocaThermalPrinter())
            return Printer.FAMILY_BOCA;
        if (this.isZebraCardPrinter() || this.isCardPrinter())
            return Printer.FAMILY_ZEBRA_CARD;
        const mm = this.cupsMakeAndModel();
        if (/zebra/i.test(mm) && !this.isZebraCardPrinter())
            return Printer.FAMILY_ZEBRA_THERMAL;
        return Printer.FAMILY_OTHER;
    }

    supportsRotate180() {
        const family = this.printerFamily();
        return family === Printer.FAMILY_BOCA || family === Printer.FAMILY_ZEBRA_THERMAL
            || family === Printer.FAMILY_OTHER;
    }

    /**
     * CUPS make-and-model (PiHyve sends it as options.model, like host hardware).
     *
     * @returns {string}
     */
    cupsMakeAndModel() {
        return this.deviceModel();
    }

    /**
     * IEEE-1284 device id (PiHyve sends it as options.device_id).
     *
     * @returns {string}
     */
    cupsDeviceId() {
        const raw = this.options?.device_id ?? this.device_id;
        return raw ? String(raw) : '';
    }

    /**
     * @param {string|undefined} device_id
     * @return {boolean}
     */
    static boca_device_id_matches(device_id) {
        const did = (device_id && String(device_id).toUpperCase()) || '';
        return did.indexOf('MFG:BOCA') !== -1;
    }

    /**
     * Boca FGL thermal (Lemur / FGL46, etc.), from CUPS device-id or make-and-model.
     *
     * @return {boolean}
     */
    isBocaThermalPrinter() {
        if (Printer.boca_device_id_matches(this.cupsDeviceId()))
            return true;
        const mm = this.cupsMakeAndModel();
        if (!mm || !/boca/i.test(mm))
            return false;
        return /fgl46/i.test(mm) || /\b46\s+200\b/i.test(mm);
    }

    /**
     * Cash drawer exposed as a CUPS queue (name / model / device-id heuristics when no USB device).
     *
     * @return {boolean}
     */
    isCashDrawerQueue() {
        const label = [
            this.name,
            this.cupsMakeAndModel(),
            this.cupsDeviceId(),
        ].filter(Boolean).join(' ').toLowerCase();

        if (/\b(tiroir-caisse|cash\s*drawer|cashdrawer|kassenschublade)\b/i.test(label))
            return true;
        if (/\b(tiroir|drawer)\b/.test(label) && /\b(caisse|cash|register|kasse)\b/.test(label))
            return true;

        return false;
    }
}

export default Printer;
